import { BoxGeometry, CylinderGeometry, Matrix4, Vector3, SphereGeometry, ConeGeometry, Shape, Vector2, ExtrudeGeometry, PlaneGeometry, Group } from "three";
import { addBarycentricCoordinates } from "../shader/BarycentricWireframe";
import { PrimitiveShape, REPLAY_ROOM_UID, hashMaterialProperties, ShadingMode, UTILITY_ROOM_UID, CameraLayers } from "flavr-common";
import { LINE_INSTANCE_SHADER } from "./lines/instance.shader";
import { RenderOrder } from "../RenderOrder";
import { InstanceBatch } from "./InstanceBatch";
import { InstancedLine } from "./lines/instanced.line";
import { InstanceBatchLineMaterial, createLineMaterial, getLineRenderOrder } from "./lines/lineMaterials";
import { getPrimitiveMeshMaterial } from "./geometry/MaterialsForPrimitiveMeshes";
import { TruncatedConeBatch, PrimitiveMeshBatch } from "./geometry/GeometryInstanceBatch";
import * as BufferGeometryUtils from "three/examples/jsm/utils/BufferGeometryUtils.js";
import { modelPool, determineModelNameForPrinting } from "@modules/model";
export var InstanceBatchMaterial;
(function (InstanceBatchMaterial) {
    InstanceBatchMaterial["Reduced"] = "Reduced";
    // for collider debug, TODO: check if this still finds any usage
    InstanceBatchMaterial["Wireframe"] = "Wireframe";
    InstanceBatchMaterial["BusyIndicator"] = "BusyIndicator";
})(InstanceBatchMaterial || (InstanceBatchMaterial = {}));
const PLANE_GEOMETRY = new PlaneGeometry(1, 1);
function createPrismGeometry(vertices) {
    const shape = new Shape();
    shape.moveTo(vertices[0].x, vertices[0].y);
    for (let i = 1; i < vertices.length; i++) {
        shape.lineTo(vertices[i].x, vertices[i].y);
    }
    shape.lineTo(vertices[0].x, vertices[0].y);
    const geometry = new ExtrudeGeometry(shape, {
        depth: 1,
        bevelEnabled: false
    });
    geometry.applyMatrix4(new Matrix4().setPosition(new Vector3(0, 0, -0.5)));
    return geometry;
}
/**
 * Handles all Instance-Batches (Geometry, Lines, Text) for rendering.
 */
export class InstanceBatchManager {
    constructor(rootNode) {
        this.rootNode = rootNode;
        // all primitiveMeshes rendered together in instanceBatches, grouped by absolutePathOfParent, then their shape
        this.geometryBatches = new Map();
        this.lineBatches = new Map();
        this.roomRootNodes = new Map();
        this.geometries = new Map(); // the geometries which will be used for rendering
        this.lineMaterials = {};
        // basic geometry for boxes
        const boxGeometry = new BoxGeometry(1, 1, 1, 5, 5, 5);
        boxGeometry.computeBoundingSphere();
        this.geometries.set(PrimitiveShape.Box, boxGeometry);
        // basic geometry for cylinders. we set the radius to 0.5, so d = 1
        const cylinderGeometry = new CylinderGeometry(0.5, 0.5, 1, 12, 1);
        cylinderGeometry.computeBoundingSphere();
        this.geometries.set(PrimitiveShape.Cylinder, cylinderGeometry);
        // basic geometry for spheres. we set the radius to 0.5, so d = 1
        const sphereGeometry = new SphereGeometry(0.5, 16, 16);
        sphereGeometry.computeBoundingSphere();
        this.geometries.set(PrimitiveShape.Sphere, sphereGeometry);
        // basic geometry for cones. we set the radius to 0.5, so d = 1
        const coneGeometry = new ConeGeometry(0.5, 1, 12, 1);
        coneGeometry.computeBoundingSphere();
        this.geometries.set(PrimitiveShape.Cone, coneGeometry);
        // basic geometry for pyramids. we set the radius to 0.5*Sqrt(2), so diagonal = sqrt(2) and width = depth = 1
        const pyramidGeometry = new ConeGeometry(0.5 * Math.SQRT2, 1, 4, 1, false, Math.PI / 4);
        pyramidGeometry.computeBoundingSphere();
        this.geometries.set(PrimitiveShape.Pyramid, pyramidGeometry);
        // basic geometry for pyramids. we set the radius to 0.5*Sqrt(2), so diagonal = sqrt(2) and width = depth = 1
        const prismGeometry = createPrismGeometry([new Vector2(-0.5, -0.5), new Vector2(0, 0.5), new Vector2(0.5, -0.5)]);
        prismGeometry.computeBoundingSphere();
        this.geometries.set(PrimitiveShape.Prism, BufferGeometryUtils.mergeVertices(prismGeometry));
        // basic geometry for plane.
        const planeGeometry = new PlaneGeometry(1, 1, 1, 1);
        planeGeometry.computeBoundingSphere();
        this.geometries.set(PrimitiveShape.Plane, BufferGeometryUtils.mergeVertices(planeGeometry));
        // add barycentric coordiantes for the wireframe effect
        for (const geo of this.geometries.values()) {
            addBarycentricCoordinates(geo, true);
        }
        this.lineMaterials[InstanceBatchLineMaterial.FUZZY_LINE] = createLineMaterial(InstanceBatchLineMaterial.FUZZY_LINE);
        this.lineMaterials[InstanceBatchLineMaterial.BASIC_LINE] = createLineMaterial(InstanceBatchLineMaterial.BASIC_LINE);
        this.lineMaterials[InstanceBatchLineMaterial.LASER] = createLineMaterial(InstanceBatchLineMaterial.LASER);
    }
    /// Room getters and setters ///
    getRootNodeForRoom(roomUid) {
        var _a;
        let node = this.roomRootNodes.get(roomUid);
        if (node === undefined) {
            let roomName = (_a = modelPool.getModel(roomUid)) === null || _a === void 0 ? void 0 : _a.getName();
            if (roomName === undefined) {
                console.error(`[renderer] Couldn't find room for UID ${roomUid} (${determineModelNameForPrinting(roomUid)})`);
                roomName = "unknown room";
            }
            node = new Group();
            node.name = roomName;
            node.frustumCulled = false;
            node.matrixAutoUpdate = false;
            this.rootNode.add(node);
            this.roomRootNodes.set(roomUid, node);
        }
        return node;
    }
    /// Batch getters ///
    getBusyIndicatorBatch(modelUID, onCreateBatch) {
        return this.getGeometryBatch(modelUID, PrimitiveShape.Box, InstanceBatchMaterial.BusyIndicator, (uid) => {
            const geometry = this.geometries.get(PrimitiveShape.Box);
            if (geometry === undefined) {
                throw new Error(`No geometry for shape ${PrimitiveShape.Box}`);
            }
            const batch = new PrimitiveMeshBatch(this.getRootNodeForRoom(uid), geometry, getPrimitiveMeshMaterial({ shading: ShadingMode.Unlit }));
            batch.instancedMesh.layers.set(CameraLayers.Default);
            batch.instancedMesh.layers.enable(CameraLayers.Utility);
            onCreateBatch === null || onCreateBatch === void 0 ? void 0 : onCreateBatch(batch);
            return batch;
        });
    }
    getPrimitiveMeshBatch(modelUID, shape, materialProperties, onCreateBatch) {
        const materialHash = hashMaterialProperties(materialProperties);
        return this.getGeometryBatch(modelUID, shape, materialHash, (uid) => {
            const geometry = this.geometries.get(shape);
            if (geometry === undefined) {
                throw new Error(`No geometry for shape ${shape}`);
            }
            addBarycentricCoordinates(geometry, false);
            const batch = new PrimitiveMeshBatch(this.getRootNodeForRoom(uid), geometry, getPrimitiveMeshMaterial(materialProperties), materialProperties.shading === ShadingMode.PhongWithShadows, materialProperties.castShadow, materialProperties.shading === ShadingMode.TransparentGrid ? RenderOrder.TRANSPARENT_GEOMETRY : RenderOrder.OPAQUE_GEOMETRY);
            onCreateBatch === null || onCreateBatch === void 0 ? void 0 : onCreateBatch(batch);
            return batch;
        });
    }
    getTruncatedConeBatch(modelUID, materialProperties, onCreateBatch) {
        const materialHash = hashMaterialProperties(materialProperties);
        return this.getGeometryBatch(modelUID, "TruncatedCone", materialHash, (absolutePathOfParent) => {
            const geometry = this.geometries.get(PrimitiveShape.Cylinder);
            if (!geometry) {
                throw new Error(`No geometry for shape ${PrimitiveShape.Cylinder}`);
            }
            const batch = new TruncatedConeBatch(this.getRootNodeForRoom(absolutePathOfParent), geometry, materialProperties);
            onCreateBatch === null || onCreateBatch === void 0 ? void 0 : onCreateBatch(batch);
            return batch;
        });
    }
    getLineBatch(roomUID, material, onCreateBatch) {
        const roomUid = roomUID;
        let batchesForRoom = this.lineBatches.get(roomUid);
        if (!batchesForRoom) {
            batchesForRoom = new Map();
            this.lineBatches.set(roomUid, batchesForRoom);
        }
        let batch = batchesForRoom.get(material);
        if (!batch) {
            batch = new InstanceBatch(this.getRootNodeForRoom(roomUID), PLANE_GEOMETRY.clone(), this.lineMaterials[material], LINE_INSTANCE_SHADER.attributes, getLineRenderOrder(material), InstancedLine);
            batchesForRoom.set(material, batch);
            onCreateBatch === null || onCreateBatch === void 0 ? void 0 : onCreateBatch(batch);
        }
        return batch;
    }
    /// Batch methods ///
    updateAllBatches(deleteEmptyBatches = true) {
        this.updateAllGeometryBatches(deleteEmptyBatches);
        this.updateAllLineBatches(deleteEmptyBatches);
    }
    forEachGeometryBatch(callback) {
        for (const [uid, batchesForParent] of this.geometryBatches) {
            for (const [shape, batchForShape] of batchesForParent) {
                for (const [material, batch] of batchForShape) {
                    callback(batch, material, shape, uid);
                }
            }
        }
    }
    forEachLineBatch(callback) {
        for (const [uid, batchesForRoom] of this.lineBatches) {
            for (const [material, batch] of batchesForRoom) {
                callback(batch, uid, material);
            }
        }
    }
    countBatches() {
        let count = 0;
        this.forEachGeometryBatch(() => count++);
        this.forEachLineBatch(() => count++);
        return count;
    }
    /// Reduced material ///
    getShouldBatchUseReducedMaterial(model, currentRoom, material) {
        const replayRoom = modelPool.getRoomOrThrow(REPLAY_ROOM_UID);
        const utilityRoom = modelPool.getRoomOrThrow(UTILITY_ROOM_UID);
        return (material !== InstanceBatchMaterial.BusyIndicator &&
            !model.isDescendantOfOrEquals(currentRoom) &&
            (model.getRoomDepth() > 0 || model.isRoom()) &&
            !model.isDescendantOfOrEquals(replayRoom) &&
            !model.isDescendantOfOrEquals(utilityRoom));
    }
    reducedMaterialForAllOutsideRoom(newRoom) {
        // When we change the room, we want all primitive Meshes outside the now-current room to be rendered in grey
        this.forEachGeometryBatch((batch, materialName, _shape, uid) => {
            const model = modelPool.getModel(uid);
            if (model === undefined)
                return;
            const reduced = this.getShouldBatchUseReducedMaterial(model, newRoom, materialName);
            batch.setUseReducedMaterial(reduced);
        });
        this.forEachLineBatch((batch, uid) => {
            const model = modelPool.getModel(uid);
            if (model === undefined)
                return;
            const replayRoom = modelPool.getRoom(REPLAY_ROOM_UID);
            if (replayRoom === undefined)
                return;
            batch.setVisible(replayRoom.isAncestorOfOrEquals(model) ||
                newRoom.isAncestorOfOrEquals(model) ||
                !(model.getRoomDepth() > 0 || model.isRoom()));
        });
    }
    /// Private batch helper methods ///
    getGeometryBatch(modelUID, shape, material, createBatch) {
        let batchForModelPath = this.geometryBatches.get(modelUID);
        if (batchForModelPath === undefined) {
            batchForModelPath = new Map();
            this.geometryBatches.set(modelUID, batchForModelPath);
        }
        let batchForShape = batchForModelPath.get(shape);
        if (batchForShape === undefined) {
            batchForShape = new Map();
            batchForModelPath.set(shape, batchForShape);
        }
        let batch = batchForShape.get(material);
        if (batch === undefined) {
            batch = createBatch(modelUID);
            batchForShape.set(material, batch);
        }
        return batch;
    }
    updateAllGeometryBatches(deleteEmptyBatches) {
        for (const [uid, batchesForParent] of this.geometryBatches) {
            for (const [shape, batchForShape] of batchesForParent) {
                for (const [material, batch] of batchForShape) {
                    if (batch.getNumInstances() > 0) {
                        // the batch has instances in it. update..
                        batch.update();
                    }
                    else if (deleteEmptyBatches) {
                        batch.destroy();
                        batchForShape.delete(material);
                    }
                }
                if (deleteEmptyBatches && batchForShape.size === 0) {
                    batchesForParent.delete(shape);
                }
            }
            if (deleteEmptyBatches && batchesForParent.size === 0) {
                this.geometryBatches.delete(uid);
            }
        }
    }
    updateAllLineBatches(deleteEmptyBatches) {
        for (const [absolutePathOfParent, batchesForRoom] of this.lineBatches) {
            for (const [material, batch] of batchesForRoom) {
                if (batch.getNumInstances() > 0) {
                    // the batch has instances in it. update..
                    batch.update();
                }
                else if (deleteEmptyBatches) {
                    // the batch has (become) empty. remove it
                    batch.destroy();
                    batchesForRoom.delete(material);
                }
            }
            if (deleteEmptyBatches && batchesForRoom.size === 0) {
                this.lineBatches.delete(absolutePathOfParent);
            }
        }
    }
}
